import type {
  Company,
  CompanyResume,
  ObservationResponse,
  VIRKReport,
} from "@/types/company";
import { api } from "@/utils/api";
import { queryOptions, useQuery } from "@tanstack/react-query";

// Function to fetch company data by lassoId
export const fetchCompanyByLassoId = async (
  lassoId: string,
): Promise<Company> => {
  const response = await api.get<Company>(`${lassoId}`).json();
  return response;
};

export const getCompanyQueryOptions = (lassoId?: string) => {
  return queryOptions({
    queryKey: ["company", lassoId],
    queryFn: async () => {
      if (!lassoId) throw new Error("No lassoId provided");
      return await fetchCompanyByLassoId(lassoId);
    },
    enabled: !!lassoId, // Only fetch data when lassoId is provided
    throwOnError: true, // Throw error if data fetching fails
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep unused data in cache for 30 minutes
  });
};

// TanStack Query hook for company data
export const useCompanyQuery = (lassoId?: string) => {
  return useQuery(getCompanyQueryOptions(lassoId));
};

// Function to fetch company resume data by lassoId
export const fetchCompanyResumeByLassoId = async (
  lassoId: string,
): Promise<CompanyResume> => {
  const response = await api
    .get<CompanyResume>(`modules/resume/${lassoId}?new=true`)
    .json();
  return response;
};

export const getCompanyResumeQueryOptions = (lassoId?: string) => {
  return queryOptions({
    queryKey: ["companyResume", lassoId],
    queryFn: async () => {
      if (!lassoId) throw new Error("No lassoId provided");
      return await fetchCompanyResumeByLassoId(lassoId);
    },
    enabled: !!lassoId,
  });
};

export const useCompanyResumeQuery = (lassoId?: string) => {
  return useQuery(getCompanyResumeQueryOptions(lassoId));
};

export const fetchPerformanceObservationsByLassoId = async (
  lassoId: string,
): Promise<ObservationResponse> => {
  return await api
    .get<ObservationResponse>(`apps/sales/companies/${lassoId}/performance`)
    .json();
};

export const getPerformanceObservationsQueryOptions = (lassoId?: string) => {
  return queryOptions({
    queryKey: ["performanceObservations", lassoId],
    queryFn: async () => {
      if (!lassoId) throw new Error("No lassoId provided");
      return await fetchPerformanceObservationsByLassoId(lassoId);
    },
    enabled: !!lassoId,
    throwOnError: true,
  });
};

export const usePerformanceObservationsQuery = (lassoId?: string) => {
  return useQuery(getPerformanceObservationsQueryOptions(lassoId));
};

export const fetchCompanyReportsByLassoId = async (lassoId: string) => {
  return await api.get<VIRKReport[]>(`${lassoId}/reports`).json();
};

export const getCompanyReportsQueryOptions = (lassoId?: string) => {
  return queryOptions({
    queryKey: ["companyReports", lassoId],
    queryFn: async () => {
      if (!lassoId) return undefined;
      return await fetchCompanyReportsByLassoId(lassoId);
    },
    enabled: !!lassoId,
    throwOnError: true,
  });
};

export const useCompanyReportsQuery = (lassoId?: string) => {
  return useQuery(getCompanyReportsQueryOptions(lassoId));
};
